/**
 * preloader.ts
 *
 * Background resource preloader that initiates data fetching before authentication
 *
 * Primary responsibilities:
 * - Preload essential resources in the background
 * - Cache model data and other static resources
 * - Initialize loading of UI components
 * - Manage staged loading of resources based on priority
 */

import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { LLM, LLMID, OpenRouterLLM } from '@/types'
import { fetchHostedModels, fetchOllamaModels, fetchOpenRouterModels } from './models/fetch-models'
import { Tables } from '@/supabase/types'
import { safeConsole } from '@/lib/utils/safeConsole'

// Define global interface for window caches
declare global {
  interface Window {
    __PLATFORM_TOOLS_CACHE?: {
      tools: Tables<'tools'>[]
      timestamp: number
      authState?: boolean
    }
    __PLATFORM_TOOLS_MEMORY_CACHE?: {
      tools: Tables<'tools'>[]
      timestamp: number
      authState?: boolean
    }
  }
}

// Preloader state tracking
export const preloaderStateAtom = atom({
  staticResourcesLoaded: false,
  uiComponentsLoaded: false,
  modelsMetadataLoaded: false,
  toolsMetadataLoaded: false,
  platformToolsLoaded: false,
  fullDataLoaded: false,
  preAuthLoadingComplete: false,
  postAuthLoadingComplete: false,
  errors: [] as string[]
})

// Cached models data with storage persistence (but loaded lazily)
export const cachedHostedModelsAtom = atomWithStorage<LLM[]>(
  'cached_hosted_models',
  [],
  undefined, // Use default localStorage
  {
    getOnInit: false // Don't load from storage until first read
  }
)

export const cachedLocalModelsAtom = atomWithStorage<LLM[]>(
  'cached_local_models',
  [],
  undefined, // Use default localStorage
  {
    getOnInit: false // Don't load from storage until first read
  }
)

export const cachedOpenRouterModelsAtom = atomWithStorage<OpenRouterLLM[]>(
  'cached_openrouter_models',
  [],
  undefined, // Use default localStorage
  {
    getOnInit: false // Don't load from storage until first read
  }
)

// Cached platform tools with storage persistence
export const cachedPlatformToolsAtom = atomWithStorage<Tables<'tools'>[]>(
  'cached_platform_tools',
  [],
  undefined, // Use default localStorage
  {
    getOnInit: false // Don't load from storage until first read
  }
)

// Timestamp of the last successful model fetch
export const lastModelFetchTimestampAtom = atomWithStorage<number>('last_model_fetch_timestamp', 0)

// Timestamp of the last successful platform tools fetch
export const lastPlatformToolsFetchTimestampAtom = atomWithStorage<number>(
  'platform_tools_timestamp',
  0
)

// Constants
const MODEL_CACHE_TTL = 24 * 60 * 60 * 1000 // 24 hours in milliseconds
const TOOLS_CACHE_TTL = 60 * 60 * 1000 // 1 hour in milliseconds
const MODEL_PRELOAD_DEFERRED_ROUTE_PREFIXES = ['/spaces']

/**
 * Helper to safely use requestIdleCallback with fallback
 */
const safeRequestIdleCallback = (callback: IdleRequestCallback, options = { timeout: 2000 }) => {
  if (typeof window !== 'undefined') {
    // Use requestIdleCallback if available, otherwise fallback to setTimeout
    if ('requestIdleCallback' in window) {
      return window.requestIdleCallback(callback, options)
    } else {
      // Fallback to setTimeout with a simple deadline
      return setTimeout(() => {
        callback({
          didTimeout: false,
          timeRemaining: () => 1
        })
      }, options.timeout)
    }
  }
  // Just execute the callback immediately in environments without window
  callback({
    didTimeout: false,
    timeRemaining: () => 1
  })
  return 0
}

const shouldDeferModelMetadataPreload = () => {
  if (typeof window === 'undefined') {
    return false
  }

  return MODEL_PRELOAD_DEFERRED_ROUTE_PREFIXES.some((prefix) =>
    window.location.pathname.startsWith(prefix)
  )
}

/**
 * Preload static resources and UI components (can be done without auth)
 */
export const preloadStaticResources = async () => {
  try {
    // Prefetch static assets
    if (typeof window !== 'undefined') {
      // Prefetch logo and other key brand assets
      const brandAssets = [
        '/losi-icon.png',
        '/losi-logo.png',
        '/logo.png',
        '/android-chrome-192x192.png',
        '/apple-touch-icon.png',
        '/favicon-32x32.png'
      ]

      // Create and append all prefetch links
      brandAssets.forEach((asset) => {
        const link = document.createElement('link')
        link.rel = 'prefetch'
        link.href = asset
        link.as = 'image'
        document.head.appendChild(link)
      })

      // Prefetch critical CSS
      const cssLink = document.createElement('link')
      cssLink.rel = 'preload'
      cssLink.href = '/styles/critical.css'
      cssLink.as = 'style'
      document.head.appendChild(cssLink)

      // Prefetch key routes for faster navigation
      const criticalRoutes = ['/login', '/signup', '/features', '/about']
      criticalRoutes.forEach((route) => {
        const routeLink = document.createElement('link')
        routeLink.rel = 'prefetch'
        routeLink.href = route
        routeLink.as = 'document'
        document.head.appendChild(routeLink)
      })
    }

    // Return success
    return { success: true }
  } catch (error) {
    console.error('Error preloading static resources:', error)
    return { success: false, error }
  }
}

/**
 * Initialize background loading of model metadata
 * This can be done even before auth
 */
export const preloadModelMetadata = async (setAtomFn: Function) => {
  try {
    // Check if we have cached models and if they're still valid
    const lastFetchTimestamp = Number(localStorage.getItem('last_model_fetch_timestamp') || '0')
    const now = Date.now()

    // If cache is still valid, use it
    if (now - lastFetchTimestamp < MODEL_CACHE_TTL) {
      const cachedHostedModels = JSON.parse(localStorage.getItem('cached_hosted_models') || '[]')
      const cachedLocalModels = JSON.parse(localStorage.getItem('cached_local_models') || '[]')
      const cachedOpenRouterModels = JSON.parse(
        localStorage.getItem('cached_openrouter_models') || '[]'
      )

      if (cachedHostedModels.length > 0) {
        setAtomFn({
          cachedHostedModels,
          cachedLocalModels,
          cachedOpenRouterModels,
          loadedFromCache: true
        })

        return {
          success: true,
          cached: true,
          models: {
            hosted: cachedHostedModels,
            local: cachedLocalModels,
            openRouter: cachedOpenRouterModels
          }
        }
      }
    }

    if (shouldDeferModelMetadataPreload()) {
      return {
        success: true,
        cached: false,
        deferred: true
      }
    }

    // If cache is invalid or empty, we'll return but initiate a background fetch
    // This will be updated once auth is complete
    safeRequestIdleCallback(
      (deadline) => {
        // Just initiate the network requests so they start loading
        // Results will be properly used after auth
        fetchHostedModels(null)
        fetchOllamaModels()
        fetchOpenRouterModels()
      },
      { timeout: 2000 }
    )

    return { success: true, cached: false }
  } catch (error) {
    console.error('Error preloading model metadata:', error)
    return { success: false, error }
  }
}

/**
 * Preload platform tools from cache
 */
export const preloadPlatformTools = async (setAtomFn: Function) => {
  try {
    // Check if we have cached tools and if they're still valid
    const lastFetchTimestamp = Number(localStorage.getItem('platform_tools_timestamp') || '0')
    const cachedAuthState = localStorage.getItem('platform_tools_auth_state')
    const now = Date.now()

    // If cache is still valid, use it
    if (now - lastFetchTimestamp < TOOLS_CACHE_TTL) {
      const cachedTools = localStorage.getItem('cached_platform_tools')

      if (cachedTools) {
        const tools = JSON.parse(cachedTools)
        if (tools.length > 0) {
          safeConsole.log(
            `Preloading ${tools.length} platform tools from cache with auth state: ${cachedAuthState}`
          )
          setAtomFn(tools)

          // Also set up the window cache for faster access
          if (typeof window !== 'undefined') {
            window.__PLATFORM_TOOLS_CACHE = {
              tools,
              timestamp: lastFetchTimestamp,
              authState: cachedAuthState === 'true'
            }

            // Set in-memory cache immediately to prevent duplicate fetching
            if (typeof window.__PLATFORM_TOOLS_MEMORY_CACHE === 'undefined') {
              window.__PLATFORM_TOOLS_MEMORY_CACHE = {
                tools,
                timestamp: lastFetchTimestamp,
                authState: cachedAuthState === 'true'
              }
            }
          }

          return {
            success: true,
            cached: true,
            tools,
            authState: cachedAuthState === 'true'
          }
        }
      }
    } else if (lastFetchTimestamp > 0) {
      safeConsole.log('Platform tools cache expired, will fetch from API')
    }

    return { success: true, cached: false }
  } catch (error) {
    console.error('Error preloading platform tools:', error)
    return { success: false, error }
  }
}

/**
 * Load models based on authenticated profile
 * Should be called after auth is complete
 */
export const loadModelsWithProfile = async (
  profile: Tables<'profiles'> | null | undefined,
  setHostedModels: (models: LLM[]) => void,
  setLocalModels: (models: LLM[]) => void,
  setOpenRouterModels: (models: OpenRouterLLM[]) => void,
  setEnvKeyMap: (keyMap: Record<string, any>) => void,
  setTimestamp: (timestamp: number) => void
) => {
  try {
    // Start all fetch operations in parallel
    const [hostedResult, localResult, openRouterResult] = await Promise.allSettled([
      fetchHostedModels(profile),
      fetchOllamaModels(),
      fetchOpenRouterModels()
    ])

    // Process hosted models
    if (hostedResult.status === 'fulfilled' && hostedResult.value) {
      const { hostedModels, envKeyMap } = hostedResult.value
      setHostedModels(hostedModels || [])
      setEnvKeyMap(envKeyMap || {})

      // Cache models for future use
      localStorage.setItem('cached_hosted_models', JSON.stringify(hostedModels || []))
    }

    // Process local models
    if (localResult.status === 'fulfilled' && localResult.value) {
      setLocalModels(localResult.value)
      localStorage.setItem('cached_local_models', JSON.stringify(localResult.value || []))
    }

    // Process OpenRouter models
    if (openRouterResult.status === 'fulfilled' && openRouterResult.value) {
      setOpenRouterModels(openRouterResult.value)
      localStorage.setItem('cached_openrouter_models', JSON.stringify(openRouterResult.value || []))
    }

    // Set timestamp of this fetch
    const timestamp = Date.now()
    setTimestamp(timestamp)
    localStorage.setItem('last_model_fetch_timestamp', timestamp.toString())

    return { success: true }
  } catch (error) {
    console.error('Error loading models with profile:', error)
    return { success: false, error }
  }
}

/**
 * Main preloader function that orchestrates background loading
 * Call this as early as possible in the app lifecycle
 */
export const initBackgroundPreloading = () => {
  if (typeof window === 'undefined') {
    return
  }

  if (
    'serviceWorker' in navigator &&
    process.env.NODE_ENV === 'production'
  ) {
    safeRequestIdleCallback(() => {
      navigator.serviceWorker
        .register('/sw.js', { updateViaCache: 'none' })
        .catch((error) => {
          console.error('Service worker registration failed:', error)
        })
    })
  }
}
